// Public landing page — port of `4m-Immobilien design pages/landing.jsx`
// (Promo · Öffentliche Website). Intentionally dark (#0a0a0a canvas, gold
// #c9a25f accents, Fraunces display); the global theme toggle does not
// affect this page — it's a brand surface, not an app surface.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchMe, type MeResponse } from '@/lib/auth';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { BlurText, MagicRings, ScrollReveal, AnimatedList } from '@/components/anim';
import { COMPANY } from '@4m/shared';

export const Route = createFileRoute('/')({
  component: HomePage,
});

const GOLD = '#c9a25f';
const GOLD_DEEP = '#a07f3f';
const INK = '#1a1305';
const CANVAS = '#0a0a0a';
const SURFACE = '#1a1715';
const TEXT = '#f5f1ea';
const TEXT_DIM = '#d4cec3';
const TEXT_MUTED = '#a8a29a';
const TEXT_FAINT = '#7a7068';

function HomePage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    fetchMe()
      .then((m) => setMe(m))
      .catch(() => setMe(null));
    // Force-mount as dark for this surface; restore on unmount.
    const html = document.documentElement;
    const wasDark = html.classList.contains('dark');
    html.classList.add('dark');
    return () => {
      if (!wasDark) html.classList.remove('dark');
    };
  }, []);

  const goApp = () => void navigate({ to: '/dashboard' });
  const goLogin = () => void navigate({ to: '/login' });

  return (
    <main
      style={{
        background: CANVAS,
        color: TEXT,
        fontFamily: 'Inter, system-ui, sans-serif',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <LandingNav me={me} onOpenApp={goApp} onLogin={goLogin} />
      <LandingHero onLogin={goLogin} />
      <LandingProductShowcase />
      <LandingFeatures />
      <LandingWorkflow />
      <LandingUSP />
      <LandingMetrics />
      <LandingMobile />
      <LandingSecurity />
      <LandingBeforeAfter />
      <LandingCTA />
      <LandingFooter />
    </main>
  );
}

// ─── Nav ────────────────────────────────────────────────────────────────
function LandingNav({
  me,
  onOpenApp,
  onLogin,
}: {
  me: MeResponse | null;
  onOpenApp: () => void;
  onLogin: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(18px) saturate(140%)',
        WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        background: 'rgba(10,10,10,0.72)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
        }}
        className="md:!px-12"
      >
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: TEXT,
            textDecoration: 'none',
          }}
        >
          <img
            src="/brand/logo-full.png"
            alt="4M Immobilien"
            style={{ height: 38, width: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </Link>

        <nav
          className="hidden lg:flex"
          style={{
            gap: 28,
            fontSize: 14,
            color: TEXT_DIM,
          }}
        >
          <a href="#showcase" style={navLink}>{t('home.nav.platform', 'Plattform')}</a>
          <a href="#features" style={navLink}>{t('home.nav.features', 'Funktionen')}</a>
          <a href="#team" style={navLink}>{t('home.nav.about', 'Über uns')}</a>
          <a href="#cta" style={navLink}>{t('home.nav.contact', 'Kontakt')}</a>
        </nav>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div
            className="hidden md:flex"
            style={{
              fontSize: 13,
              color: TEXT_MUTED,
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: '#5cb87f',
                boxShadow: '0 0 8px #5cb87f',
              }}
            />
            {t('home.nav.live', 'System online')}
          </div>
          <LanguageSwitcher />
          {me ? (
            <button
              type="button"
              onClick={onOpenApp}
              style={{ ...btnGold, padding: '9px 18px' }}
            >
              {t('home.cta.open_app', 'Zum CRM')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onLogin}
                style={btnOutline}
                className="hidden sm:inline-flex"
              >
                {t('home.nav.cta_login', 'Mitarbeiter-Login')}
              </button>
              <button
                type="button"
                onClick={() => {
                  document.querySelector('#cta')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{ ...btnGold, padding: '9px 18px' }}
                className="hidden sm:inline-flex"
              >
                {t('home.nav.cta_consult', 'Beratung anfragen')}
              </button>
              {/* Mobile-only single Login button */}
              <button
                type="button"
                onClick={onLogin}
                style={{ ...btnGold, padding: '9px 14px', fontSize: 12 }}
                className="sm:hidden"
              >
                {t('home.nav.cta_login_short', 'Login')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Hero ───────────────────────────────────────────────────────────────
function LandingHero({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation();
  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: CANVAS,
      }}
    >
      {/* Ambient gold wash — bleeds to viewport edges, no hard clip */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 60% 70% at 80% 0%, rgba(201,162,95,0.18) 0%, transparent 65%), radial-gradient(ellipse 70% 60% at 10% 90%, rgba(201,162,95,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      {/* Concentric rings — softly faded, positioned to viewport */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(140vh, 1600px)',
          height: 'min(140vh, 1600px)',
          opacity: 0.5,
          pointerEvents: 'none',
          maskImage:
            'radial-gradient(circle, #000 0%, #000 45%, transparent 75%)',
          WebkitMaskImage:
            'radial-gradient(circle, #000 0%, #000 45%, transparent 75%)',
        }}
      >
        <MagicRings size={1200} rings={8} opacity={0.07} color={GOLD} />
      </div>

      <div
        style={{
          position: 'relative',
          maxWidth: 1280,
          margin: '0 auto',
          padding: '60px 24px 80px',
        }}
        className="md:!px-12 md:!py-[100px]"
      >
        <div
          className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]"
          style={{ gap: 60, alignItems: 'center', position: 'relative' }}
        >
        <div>
          <ScrollReveal>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                background: 'rgba(201,162,95,0.12)',
                border: '1px solid rgba(201,162,95,0.3)',
                borderRadius: 999,
                fontSize: 12,
                color: GOLD,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: GOLD,
                }}
              />
              {t('home.hero.eyebrow', 'Maßgefertigt für 4M · Nicht öffentlich verfügbar')}
            </div>
          </ScrollReveal>

          <h1
            style={{
              margin: '24px 0 0',
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontWeight: 300,
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
              fontSize: 'clamp(36px, 8vw, 88px)',
              hyphens: 'auto',
              wordBreak: 'break-word',
            }}
            lang="de"
          >
            <BlurText className="block">{t('home.hero.line1', 'Wiens')}</BlurText>
            <BlurText
              delay={250}
              className="block"
              style={{ fontStyle: 'italic', color: GOLD }}
            >
              {t('home.hero.line2', 'diskreteste')}
            </BlurText>
            <BlurText delay={500} className="block">
              {t('home.hero.line3', 'Immobilien­agentur.')}
            </BlurText>
          </h1>

          <ScrollReveal delay={400}>
            <div
              style={{
                fontSize: 18,
                color: TEXT_DIM,
                marginTop: 28,
                maxWidth: 540,
                lineHeight: 1.55,
              }}
            >
              {t(
                'home.hero.body',
                'Wir verkaufen Wohnungen, Häuser und Anlageobjekte in den besten Lagen Wiens — unterstützt von einer eigens für uns entwickelten Plattform, wie sie keine andere Agentur besitzt.',
              )}
            </div>
          </ScrollReveal>

          <ScrollReveal delay={600}>
            <div className="flex flex-col sm:flex-row" style={{ gap: 12, marginTop: 36 }}>
              <button
                type="button"
                onClick={() => {
                  document.querySelector('#cta')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  ...btnGold,
                  padding: '14px 24px',
                  fontSize: 14.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  justifyContent: 'center',
                }}
              >
                {t('home.hero.cta_primary', 'Immobilie anbieten')}
                <ArrowSvg />
              </button>
              <button
                type="button"
                onClick={() => {
                  document.querySelector('#showcase')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  padding: '14px 22px',
                  background: 'rgba(255,255,255,0.06)',
                  color: TEXT,
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 10,
                  fontSize: 14.5,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t('home.hero.cta_secondary', 'Plattform ansehen')}
              </button>
            </div>
          </ScrollReveal>

          <AnimatedList
            startDelay={800}
            itemDelay={120}
            className="grid grid-cols-3 gap-4 sm:gap-8"
            style={{ marginTop: 40 }}
          >
            {[
              { n: '€480M+', l: t('home.hero.stat_volume', 'Vermitteltes Volumen') },
              { n: '22 J.', l: t('home.hero.stat_years', 'Jahre an einer Adresse') },
              { n: '94%', l: t('home.hero.stat_repeat', 'Wiederkäufer-Quote') },
            ].map((s) => (
              <div
                key={s.l}
                style={{ borderLeft: `2px solid ${GOLD}`, paddingLeft: 12 }}
              >
                <div
                  style={{
                    fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                    fontSize: 'clamp(20px, 5vw, 32px)',
                    color: TEXT,
                    letterSpacing: '-0.025em',
                    lineHeight: 1,
                  }}
                >
                  {s.n}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: TEXT_MUTED,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginTop: 6,
                  }}
                >
                  {s.l}
                </div>
              </div>
            ))}
          </AnimatedList>
        </div>

        {/* Right: featured property card */}
        <div style={{ position: 'relative' }}>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -60,
              right: -60,
              bottom: -60,
              left: -60,
              background:
                'radial-gradient(circle at 70% 30%, rgba(201,162,95,0.22) 0%, transparent 65%)',
              pointerEvents: 'none',
              filter: 'blur(8px)',
            }}
          />
          <ScrollReveal delay={300}>
            <FeaturedListingCard onLogin={onLogin} />
          </ScrollReveal>
        </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedListingCard({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: SURFACE,
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 18,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
      }}
    >
      <div
        style={{
          height: 320,
          position: 'relative',
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.5) 100%), linear-gradient(135deg, #6b5840 0%, #2d2620 70%, #1a1413 100%)',
        }}
      >
        {/* Building windows */}
        <div
          className="absolute inset-[30px_30px_70px_30px] sm:!inset-[40px_60px_80px_60px]"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 14,
          }}
        >
          {Array.from({ length: 16 }).map((_, i) => (
            <div
              key={i}
              style={{
                aspectRatio: '0.65',
                background:
                  i % 5 === 0 ? 'rgba(255,220,150,0.45)' : 'rgba(255,220,150,0.08)',
                borderRadius: 2,
                border: '1px solid rgba(255,220,150,0.15)',
              }}
            />
          ))}
        </div>

        {/* Top labels */}
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              padding: '5px 10px',
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: TEXT,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {t('home.listing.off_market', 'Off-Market exklusiv')}
          </span>
          <span
            style={{
              padding: '5px 10px',
              background: 'rgba(201,162,95,0.95)',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: INK,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {t('home.listing.new', 'Neu · 4. Mai')}
          </span>
        </div>

        {/* Bottom label on photo */}
        <div style={{ position: 'absolute', bottom: 18, left: 20, right: 20 }}>
          <div
            style={{
              fontSize: 11,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              fontWeight: 600,
            }}
          >
            Cobenzlgasse 14 · 1190 Döbling
          </div>
          <div
            style={{
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontSize: 28,
              marginTop: 4,
              letterSpacing: '-0.02em',
            }}
          >
            Altbau-Dachgeschoss{' '}
            <span style={{ fontStyle: 'italic', color: GOLD }}>mit Terrasse</span>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '20px 24px',
          display: 'flex',
          gap: 24,
          alignItems: 'baseline',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: TEXT_MUTED,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            {t('home.listing.price', 'Verkaufspreis')}
          </div>
          <div
            style={{
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontSize: 32,
              marginTop: 2,
              letterSpacing: '-0.025em',
            }}
          >
            € 1,84 Mio.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, fontSize: 13, color: TEXT_DIM }}>
          <Spec label={t('home.listing.area', 'Fläche')} value="148 m²" />
          <Spec label={t('home.listing.rooms', 'Zimmer')} value="4" />
          <Spec label={t('home.listing.terrace', 'Terrasse')} value="32 m²" />
        </div>
      </div>

      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            background: GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: INK,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          AH
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>Andreas Huber</div>
          <div style={{ fontSize: 11, color: TEXT_MUTED }}>
            {t('home.listing.role', 'Senior Advisor')}
          </div>
        </div>
        <button
          type="button"
          onClick={onLogin}
          style={{
            padding: '8px 14px',
            background: GOLD,
            color: INK,
            border: 'none',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('home.listing.viewing', 'Diskret anfragen')}
        </button>
      </div>
      {/* Trust strip — ★4,9 rating + discreet badge + 24/7 Zoki tagline.
          Renders below the agent/CTA row to push the social proof + KI USP
          right next to the price without crowding the photo area. */}
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '12px 24px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          fontSize: 11,
          color: TEXT_MUTED,
        }}
      >
        <span style={{ color: GOLD, letterSpacing: '0.04em' }}>
          ★★★★★ <span style={{ color: TEXT, fontWeight: 600 }}>{t('home.listing.rating', '4,9 · 1.210 Bewertungen')}</span>
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>🔒 {t('home.listing.trust', 'Diskrete Anfrage')}</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>{t('home.listing.ki_line', '24/7 erreichbar mit KI-Assistent Zoki')}</span>
      </div>
    </div>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span style={{ color: TEXT_FAINT }}>{label}</span>
      <div style={{ fontWeight: 600, color: TEXT, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ─── Trust ──────────────────────────────────────────────────────────────
// ─── Product Showcase ───────────────────────────────────────────────────
function LandingProductShowcase() {
  const { t } = useTranslation();
  return (
    <section id="showcase" style={{ position: 'relative' }}>
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 pt-16 pb-12 sm:px-8 sm:pt-20 sm:pb-16 md:!pt-[120px] md:!pb-[80px] md:!px-12"
      >
      <ScrollReveal style={{ textAlign: 'center', marginBottom: 56 }}>
        <div
          style={{
            fontSize: 12,
            color: GOLD,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            fontWeight: 700,
          }}
        >
          {t('home.showcase.eyebrow', 'Die 4M-Plattform')}
        </div>
        <h2
          style={{
            margin: '12px 0 0',
            fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
            fontWeight: 300,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            fontSize: 'clamp(34px, 5vw, 64px)',
          }}
        >
          {t('home.showcase.title_lead', 'Software, die ')}
          <span style={{ fontStyle: 'italic', color: GOLD }}>
            {t('home.showcase.title_accent', 'nur wir')}
          </span>{' '}
          {t('home.showcase.title_tail', 'haben.')}
        </h2>
        <p
          style={{
            fontSize: 17,
            color: TEXT_MUTED,
            marginTop: 18,
            maxWidth: 640,
            margin: '18px auto 0',
            lineHeight: 1.55,
          }}
        >
          {t(
            'home.showcase.body',
            'Kein Standard-CRM, keine Mietsoftware. Eine eigens für 4M Immobilien entwickelte Plattform — angepasst an unsere Workflows, unsere Kunden, das österreichische Recht.',
          )}
        </p>
      </ScrollReveal>

      <ScrollReveal>
        <div
          style={{
            background:
              'linear-gradient(135deg, rgba(201,162,95,0.08) 0%, transparent 60%)',
            padding: 32,
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.06)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              height: 600,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(201,162,95,0.15) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              borderRadius: 12,
              margin: '0 -8px',
              padding: '0 8px',
            }}
            className="fm-noscroll"
          >
            <div style={{ minWidth: 720 }}>
              <MiniDashboardPreview />
            </div>
          </div>

          {/* Floating callouts — visible on lg+ */}
          <FloatingCallout
            className="hidden lg:block"
            style={{ top: 80, right: 56 }}
            label={t('home.showcase.callout1_label', 'KI-Assistent')}
            body={t(
              'home.showcase.callout1_body',
              'Zoki erstellt Briefings, matcht Käufer und schreibt Exposés.',
            )}
          />
          <FloatingCallout
            className="hidden lg:block"
            style={{ bottom: 90, left: 56 }}
            label={t('home.showcase.callout2_label', 'Klienten-Schutz')}
            body={t(
              'home.showcase.callout2_body',
              'Ownership-Lock verhindert Klienten-Diebstahl bei Makler-Wechsel.',
            )}
          />
        </div>
      </ScrollReveal>
      </div>
    </section>
  );
}

function FloatingCallout({
  label,
  body,
  style,
  className,
}: {
  label: string;
  body: string;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        background: SURFACE,
        border: `1px solid rgba(201,162,95,0.4)`,
        borderRadius: 12,
        padding: '12px 16px',
        maxWidth: 220,
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          color: GOLD,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>{body}</div>
    </div>
  );
}

function MiniDashboardPreview() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: CANVAS,
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.08)',
        overflow: 'hidden',
        height: 480,
        display: 'flex',
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 60,
          background: SURFACE,
          borderRight: '1px solid rgba(255,255,255,0.05)',
          padding: '20px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          flexShrink: 0,
        }}
      >
        <img
          src="/brand/logo-icon-256.png"
          alt="4M"
          width={30}
          height={30}
          style={{ display: 'block', objectFit: 'contain' }}
        />
        <div style={{ height: 1, width: 28, background: 'rgba(255,255,255,0.08)' }} />
        {[true, false, false, false, false, false].map((active, i) => (
          <div
            key={i}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: active ? 'rgba(201,162,95,0.18)' : 'transparent',
              border: active ? '1px solid rgba(201,162,95,0.3)' : 'none',
            }}
          />
        ))}
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: 24, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif', fontSize: 26, color: TEXT }}>
              Dashboard
            </div>
            <div style={{ fontSize: 12, color: TEXT_FAINT, marginTop: 2 }}>
              Wien-Mitte · Mittwoch, 4. Mai
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div
              style={{
                padding: '6px 12px',
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 6,
                fontSize: 11,
                color: TEXT_MUTED,
              }}
            >
              09:14
            </div>
            <div
              style={{
                padding: '6px 12px',
                background: GOLD,
                borderRadius: 6,
                fontSize: 11,
                color: INK,
                fontWeight: 600,
              }}
            >
              + {t('home.showcase.new', 'Neu')}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
          className="md:!grid-cols-4 md:!gap-3"
        >
          {[
            { l: t('home.showcase.kpi1', 'Aktive Klienten'), v: '1.247', d: '+38' },
            { l: t('home.showcase.kpi2', 'Aktive Immobilien'), v: '184', d: '+5' },
            { l: t('home.showcase.kpi3', 'Volumen Q2'), v: '€4,2 M', d: '+12%' },
            { l: t('home.showcase.kpi4', 'Termine heute'), v: '23', d: '4 pending' },
          ].map((k) => (
            <div
              key={k.l}
              style={{
                padding: 14,
                background: SURFACE,
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: TEXT_FAINT,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {k.l}
              </div>
              <div
                style={{
                  fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                  fontSize: 22,
                  color: TEXT,
                  marginTop: 4,
                }}
              >
                {k.v}
              </div>
              <div style={{ fontSize: 10, color: '#5cb87f', marginTop: 2 }}>{k.d}</div>
            </div>
          ))}
        </div>

        <div
          style={{ display: 'grid', gap: 10 }}
          className="md:!grid-cols-[1.2fr_1fr] md:!gap-3"
        >
          <div
            style={{
              padding: 16,
              background: SURFACE,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: TEXT_MUTED,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 12,
              }}
            >
              {t('home.showcase.chart_title', 'Volumen · letzte 12 Monate')}
            </div>
            <svg viewBox="0 0 320 140" style={{ width: '100%', height: 140 }}>
              <defs>
                <linearGradient id="grad1" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity="0.4" />
                  <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,100 L26,90 L52,95 L78,75 L104,80 L130,60 L156,55 L182,40 L208,48 L234,30 L260,35 L286,18 L320,22 L320,140 L0,140 Z"
                fill="url(#grad1)"
              />
              <path
                d="M0,100 L26,90 L52,95 L78,75 L104,80 L130,60 L156,55 L182,40 L208,48 L234,30 L260,35 L286,18 L320,22"
                fill="none"
                stroke={GOLD}
                strokeWidth={2}
              />
              {[100, 90, 95, 75, 80, 60, 55, 40, 48, 30, 35, 18, 22].map((y, i) => (
                <circle key={i} cx={i * 26} cy={y} r="2.5" fill={GOLD} />
              ))}
            </svg>
          </div>

          <div
            style={{
              padding: 16,
              background: SURFACE,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: TEXT_MUTED,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 12,
              }}
            >
              {t('home.showcase.today_title', 'Heutige Termine')}
            </div>
            {[
              { time: '10:00', n: 'Aufnahme · Cobenzlgasse 14', c: GOLD },
              { time: '13:30', n: 'Besichtigung · Wagner', c: '#5cb87f' },
              { time: '16:00', n: 'Notar · Anbot A-2461', c: '#6e8aa3' },
              { time: '17:30', n: 'Briefing · Yıldız', c: '#c2452f' },
            ].map((a) => (
              <div
                key={a.time}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '7px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <div style={{ width: 3, height: 24, background: a.c, borderRadius: 999 }} />
                <div
                  style={{
                    fontSize: 11.5,
                    color: TEXT_MUTED,
                    fontVariantNumeric: 'tabular-nums',
                    width: 38,
                  }}
                >
                  {a.time}
                </div>
                <div style={{ fontSize: 11.5, color: TEXT, flex: 1 }}>{a.n}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Features ──────────────────────────────────────────────────────────
function LandingFeatures() {
  const { t } = useTranslation();
  const features: Array<{ key: keyof typeof FeatureIcon; t: string; d: string }> = [
    { key: 'ai', t: t('home.feat.ai_t', 'KI-Assistent Zoki'), d: t('home.feat.ai_d', 'Tägliche Briefings, Käufer-Matching, Statistiken auf Zuruf, Termine per Sprachbefehl. In Deutsch, Englisch und Serbisch — auf Claude Sonnet 4.6.') },
    { key: 'shield', t: t('home.feat.shield_t', 'Klienten-Schutz'), d: t('home.feat.shield_d', 'Ownership-Lock, Audit-Trail und Admin-Genehmigungen verhindern, dass Klienten bei Makler-Wechsel verloren gehen.') },
    { key: 'team', t: t('home.feat.team_t', 'Multi-Makler-Workflows'), d: t('home.feat.team_d', 'Mehrere Makler arbeiten an einem Objekt mit klarer Provisions-Aufteilung über Anbot-Teilnehmer und Eskalations-Pfaden.') },
    { key: 'doc', t: t('home.feat.doc_t', 'Anbote & Magic-Link-Signatur'), d: t('home.feat.doc_d', 'Vermittlungsauftrag, Besichtigungsschein und Kaufanbot in 90 Sekunden generiert. WKO-Nebenkosten automatisch beigefügt. Klient unterschreibt per Magic-Link aus dem Posteingang oder SMS.') },
    { key: 'match', t: t('home.feat.match_t', 'Käufer-Matching'), d: t('home.feat.match_d', 'Suchprofile gegen aktive Immobilien matchen, sortiert nach Bezirk, Preis und Größe — ein Klick auf den Klienten zeigt passende Objekte.') },
    { key: 'lock', t: t('home.feat.lock_t', 'Off-Market-Pool'), d: t('home.feat.lock_d', 'Diskreter Pool exklusiver Objekte. Sichtbar nur für autorisierte Makler und ausgewählte Klienten.') },
    { key: 'phone', t: t('home.feat.phone_t', 'SMS & E-Mail aus dem Akt'), d: t('home.feat.phone_d', 'Twilio-SMS und Resend-E-Mails direkt aus dem Klienten-Akt. Vorlagen, automatische Erinnerungen, Bounce-Tracking und Abmeldungen.') },
    { key: 'check', t: t('home.feat.check_t', 'DSGVO-konform'), d: t('home.feat.check_d', 'Vollständiges Audit-Log pro Datensatz, Soft-Delete mit 90-Tage-Aufbewahrung, tägliche verschlüsselte Backups in EU-Region. Cloudflare-Infrastruktur.') },
    { key: 'mobile', t: t('home.feat.mobile_t', 'Mobil optimiert'), d: t('home.feat.mobile_d', 'Volle Plattform im mobilen Browser. PWA-installierbar auf iPhone und Android — Symbol auf dem Home-Bildschirm, App-ähnliche Erfahrung. Sprachnachrichten und Diktat eingebaut.') },
  ];
  return (
    <section id="features" style={{ position: 'relative' }}>
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 py-16 sm:px-8 sm:py-20 md:!py-[120px] md:!px-12"
      >
      <ScrollReveal>
        <div
          className="flex flex-col lg:flex-row"
          style={{ alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 40, gap: 32 }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: GOLD,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                fontWeight: 700,
              }}
            >
              {t('home.feat.eyebrow', 'Funktionsumfang')}
            </div>
            <h2
              style={{
                margin: '12px 0 0',
                fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                fontWeight: 300,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                maxWidth: 720,
                fontSize: 'clamp(32px, 4.5vw, 56px)',
              }}
            >
              {t('home.feat.title_lead', 'Eine Plattform, die ')}
              <span style={{ fontStyle: 'italic', color: GOLD }}>
                {t('home.feat.title_accent', 'jede')}
              </span>{' '}
              {t('home.feat.title_tail', 'Etappe abdeckt.')}
            </h2>
          </div>
          <div style={{ fontSize: 14, color: TEXT_MUTED, maxWidth: 320, lineHeight: 1.55 }}>
            {t(
              'home.feat.body',
              'Vom ersten Klienten-Kontakt bis zum Notartermin. Vom Off-Market-Tipp bis zum Q-Bericht. Alles in einem System.',
            )}
          </div>
        </div>
      </ScrollReveal>

      <AnimatedList
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        style={{ gap: 14, marginTop: 32 }}
        itemDelay={80}
      >
        {features.map((f) => (
          <FeatureCard key={f.t} iconKey={f.key} title={f.t} body={f.d} />
        ))}
      </AnimatedList>
      </div>
    </section>
  );
}

const FeatureIcon: Record<string, ReactNode> = {
  ai: <path d="M12 2L13.5 7L19 8L13.5 10L12 15L10.5 10L5 8L10.5 7Z M19 14L19.7 16.3L22 17L19.7 17.7L19 20L18.3 17.7L16 17L18.3 16.3Z" />,
  shield: <path d="M12 2L4 5V11C4 16 7 20 12 22C17 20 20 16 20 11V5L12 2Z" />,
  team: (
    <path d="M9 11C11 11 12.5 9.5 12.5 7.5C12.5 5.5 11 4 9 4C7 4 5.5 5.5 5.5 7.5C5.5 9.5 7 11 9 11Z M16 12C17.5 12 18.5 10.8 18.5 9.5C18.5 8.2 17.5 7 16 7C14.5 7 13.5 8.2 13.5 9.5 M3 19V18C3 15 6 13 9 13C12 13 15 15 15 18V19 M15 19V18.5C15 16.5 17 15 19 15C20.5 15 21 16 21 17.5" />
  ),
  doc: <path d="M14 2H6C5 2 4 3 4 4V20C4 21 5 22 6 22H18C19 22 20 21 20 20V8L14 2Z M14 2V8H20 M8 12H16 M8 16H16 M8 8H10" />,
  match: <path d="M5 12C5 8 8 5 12 5C16 5 19 8 19 12 M19 12L16 9 M19 12L22 9 M19 12C19 16 16 19 12 19C8 19 5 16 5 12 M5 12L8 15 M5 12L2 15" />,
  lock: <path d="M5 11H19C19.5 11 20 11.5 20 12V21C20 21.5 19.5 22 19 22H5C4.5 22 4 21.5 4 21V12C4 11.5 4.5 11 5 11Z M8 11V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7V11" />,
  phone: <path d="M22 16.9V19.9C22 20.9 21.1 21.7 20.1 21.7C9.5 21.5 1 13 0.7 2.4C0.7 1.4 1.5 0.5 2.5 0.5H5.5C6.4 0.5 7.1 1.1 7.3 2L8 5.7C8.1 6.4 7.9 7.1 7.4 7.6L6 9C7.5 11.7 9.7 13.9 12.4 15.4L13.8 14C14.3 13.5 15 13.3 15.7 13.4L19.4 14.1C20.3 14.3 22 15 22 16.9Z" />,
  check: <path d="M9 12L11 14L15 10 M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2Z" />,
  mobile: <path d="M7 2H17C18 2 19 3 19 4V20C19 21 18 22 17 22H7C6 22 5 21 5 20V4C5 3 6 2 7 2Z M10 18H14" />,
};

function FeatureCard({ iconKey, title, body }: { iconKey: string; title: string; body: string }) {
  return (
    <div
      style={{
        padding: 28,
        background: SURFACE,
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 14,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          background: 'rgba(201,162,95,0.14)',
          border: '1px solid rgba(201,162,95,0.28)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: GOLD,
          marginBottom: 18,
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {FeatureIcon[iconKey]}
        </svg>
      </div>
      <div
        style={{
          fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
          fontSize: 22,
          letterSpacing: '-0.015em',
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13.5, color: TEXT_MUTED, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

// ─── USP / Custom-made ─────────────────────────────────────────────────
function LandingUSP() {
  const { t } = useTranslation();
  const points = [
    t('home.usp.p1', 'Maßgefertigt für Wiener Off-Market-Geschäft'),
    t('home.usp.p2', 'Ownership-Modell schützt Klienten vor Makler-Wechsel'),
    t('home.usp.p3', 'KI-Assistent in DE / EN / SR — auf unsere Workflows abgestimmt'),
    t('home.usp.p4', 'Magic-Link-Signatur und WKO-Anlage automatisch beim Anbot'),
  ];
  return (
    <section
      style={{
        padding: '100px 24px',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #15110d 50%, #0a0a0a 100%)',
        borderTop: '1px solid rgba(201,162,95,0.15)',
        borderBottom: '1px solid rgba(201,162,95,0.15)',
      }}
      className="md:!py-[120px] md:!px-12"
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          className="grid grid-cols-1 lg:grid-cols-2"
          style={{ gap: 60, alignItems: 'center' }}
        >
          <ScrollReveal>
            <div
              style={{
                fontSize: 12,
                color: GOLD,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                fontWeight: 700,
              }}
            >
              {t('home.usp.eyebrow', 'Was uns einzigartig macht')}
            </div>
            <h2
              style={{
                margin: '12px 0 0',
                fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                fontWeight: 300,
                letterSpacing: '-0.03em',
                lineHeight: 1.04,
                fontSize: 'clamp(32px, 4.8vw, 60px)',
              }}
            >
              {t('home.usp.title_lead', 'Keine andere Wiener Agentur arbeitet so wie ')}
              <span style={{ fontStyle: 'italic', color: GOLD }}>
                {t('home.usp.title_accent', 'wir.')}
              </span>
            </h2>
            <p
              style={{
                fontSize: 16.5,
                color: TEXT_DIM,
                marginTop: 24,
                lineHeight: 1.6,
              }}
            >
              {t(
                'home.usp.body',
                'Standard-CRMs sind für irgendwen gemacht. Unsere Plattform wurde von Grund auf für 4M Immobilien entwickelt — unsere Prozesse, unsere Klientenstruktur, unsere Compliance-Anforderungen. Sie ist nicht zu kaufen. Nicht zu mieten. Sie gehört uns.',
              )}
            </p>

            <AnimatedList
              style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 16 }}
              itemDelay={120}
            >
              {points.map((p) => (
                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      background: 'rgba(201,162,95,0.18)',
                      border: '1px solid rgba(201,162,95,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: GOLD,
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div style={{ fontSize: 15, color: TEXT }}>{p}</div>
                </div>
              ))}
            </AnimatedList>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <USPCompareTable />
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

function USPCompareTable() {
  const { t } = useTranslation();
  const rows: Array<{ label: string; us: true; std: false | 'partial' }> = [
    { label: t('home.usp.row1', 'Klienten-Ownership-Lock mit Transfer-Genehmigung'), us: true, std: false },
    { label: t('home.usp.row2', 'Multi-Makler-Provisionen pro Anbot'), us: true, std: 'partial' },
    { label: t('home.usp.row3', 'KI-Assistent in DE / EN / SR'), us: true, std: 'partial' },
    { label: t('home.usp.row4', 'Magic-Link-Signatur für Anbote'), us: true, std: false },
    { label: t('home.usp.row5', 'Off-Market-Pool'), us: true, std: false },
    { label: t('home.usp.row6', 'Audit-Trail nach §6 ImmoWAG'), us: true, std: 'partial' },
    { label: t('home.usp.row7', 'Mobil-optimierte PWA'), us: true, std: 'partial' },
    { label: t('home.usp.row8', 'Maßgefertigt für 4M'), us: true, std: false },
  ];
  return (
    <div
      style={{
        background: SURFACE,
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr 1fr',
          padding: '16px 22px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          fontSize: 11,
          color: TEXT_MUTED,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontWeight: 700,
        }}
      >
        <div>{t('home.usp.col_func', 'Funktion')}</div>
        <div style={{ textAlign: 'center', color: GOLD }}>4M-Plattform</div>
        <div style={{ textAlign: 'center' }}>{t('home.usp.col_std', 'Standard-CRMs')}</div>
      </div>
      {rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr',
            padding: '14px 22px',
            borderBottom:
              i < rows.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: 13.5, color: TEXT }}>{r.label}</div>
          <div style={{ textAlign: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: 999,
                background: 'rgba(92,184,127,0.18)',
                color: '#5cb87f',
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
          </div>
          <div style={{ textAlign: 'center' }}>
            {r.std === false && <span style={{ fontSize: 18, color: '#c2452f' }}>—</span>}
            {r.std === 'partial' && (
              <span style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600 }}>
                {t('home.usp.partial', 'teils')}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Mobile teaser ─────────────────────────────────────────────────────
function LandingMobile() {
  const { t } = useTranslation();
  return (
    <section id="mobile" style={{ position: 'relative' }}>
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 py-16 sm:px-8 sm:py-20 md:!py-[120px] md:!px-12"
      >
      <div
        className="grid grid-cols-1 lg:grid-cols-2"
        style={{ gap: 60, alignItems: 'center' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            order: 2,
          }}
          className="lg:!order-1"
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: -40,
              background:
                'radial-gradient(circle, rgba(201,162,95,0.18) 0%, transparent 60%)',
              pointerEvents: 'none',
            }}
          />
          <ScrollReveal>
            <PhoneMock />
          </ScrollReveal>
        </div>
        <div className="lg:!order-2">
          <ScrollReveal>
            <div
              style={{
                fontSize: 12,
                color: GOLD,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                fontWeight: 700,
              }}
            >
              {t('home.mobile.eyebrow', 'Mobil · PWA')}
            </div>
            <h2
              style={{
                margin: '12px 0 0',
                fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                fontWeight: 300,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                fontSize: 'clamp(32px, 4.5vw, 56px)',
              }}
            >
              {t('home.mobile.title_lead', 'Das Büro ')}
              <span style={{ fontStyle: 'italic', color: GOLD }}>
                {t('home.mobile.title_accent', 'in der Tasche.')}
              </span>
            </h2>
            <p style={{ fontSize: 16.5, color: TEXT_DIM, marginTop: 24, lineHeight: 1.6 }}>
              {t(
                'home.mobile.body',
                'Die volle Plattform im mobilen Browser — installierbar als PWA auf iPhone und Android. Aufnahmen vor Ort, Termine zwischen Besichtigungen, Sprachnachrichten und Diktat im Team-Chat. Kein Store-Download, keine Updates zu installieren.',
              )}
            </p>
          </ScrollReveal>
          <AnimatedList
            className="grid grid-cols-1 sm:grid-cols-2"
            style={{ gap: 14, marginTop: 32 }}
            itemDelay={100}
          >
            {[
              {
                t: t('home.mobile.f1_t', 'PWA-installierbar'),
                d: t('home.mobile.f1_d', 'Symbol auf den Home-Bildschirm — App-ähnliche Oberfläche, Vollbild ohne Browser-Leiste.'),
              },
              {
                t: t('home.mobile.f2_t', 'Foto-Upload aus der Aufnahme'),
                d: t('home.mobile.f2_d', 'Direkt aus der Kamera ins Exposé. Bilder werden client-seitig als WebP komprimiert, bevor sie hochgeladen werden.'),
              },
              {
                t: t('home.mobile.f3_t', 'Morgenbriefing per E-Mail'),
                d: t('home.mobile.f3_d', 'Jeden Werktag um 06:00 Wien: Tag-Termine, anstehende Aktionen, neue Leads — direkt im Posteingang.'),
              },
              {
                t: t('home.mobile.f4_t', 'Sprachnachrichten & Diktat'),
                d: t('home.mobile.f4_d', 'Audio-Messages im Team-Chat. Diktieren statt Tippen — Web Speech API in DE / EN / SR.'),
              },
            ].map((f) => (
              <div
                key={f.t}
                style={{
                  padding: 18,
                  background: SURFACE,
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div style={{ fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif', fontSize: 18 }}>{f.t}</div>
                <div style={{ fontSize: 12.5, color: TEXT_MUTED, marginTop: 4 }}>{f.d}</div>
              </div>
            ))}
          </AnimatedList>
        </div>
      </div>
      </div>
    </section>
  );
}

function PhoneMock() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        width: 300,
        height: 620,
        background: '#000',
        borderRadius: 42,
        padding: 10,
        boxShadow:
          '0 30px 80px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.08)',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 32,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #faf7f2, #f3ede3)',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 90,
            height: 26,
            background: '#000',
            borderRadius: 999,
            zIndex: 5,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 24,
            right: 24,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            fontWeight: 600,
            color: '#1a1715',
            zIndex: 4,
          }}
        >
          <span>09:14</span>
          <span style={{ display: 'flex', gap: 4 }}>
            <span>•••</span>
          </span>
        </div>

        <div style={{ paddingTop: 50, padding: '50px 18px 0' }}>
          <div
            style={{
              fontSize: 11,
              color: '#7a7068',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              fontWeight: 600,
            }}
          >
            {t('home.mobile.phone_date', 'Mittwoch, 4. Mai')}
          </div>
          <div
            style={{
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontSize: 28,
              color: '#1a1715',
              marginTop: 4,
              letterSpacing: '-0.02em',
            }}
          >
            {t('home.mobile.phone_greet', 'Servus, Andreas')}
          </div>

          <div
            style={{
              marginTop: 18,
              padding: 14,
              background: 'linear-gradient(135deg, #1a1715, #2d2620)',
              borderRadius: 14,
              color: TEXT,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: GOLD,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: INK,
                }}
              >
                ✦
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: GOLD,
                }}
              >
                Zoki · {t('home.mobile.phone_briefing', 'Briefing')}
              </span>
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45 }}>
              {t(
                'home.mobile.phone_body',
                '3 Termine, 5 Aktionen. Cobenzlgasse-Aufnahme um 10:00 — Eigentümerakte vorbereitet.',
              )}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 11,
                color: '#7a7068',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {t('home.mobile.phone_today', 'Heute · 3 Termine')}
            </div>
            {[
              { time: '10:00', n: 'Aufnahme · Cobenzlgasse', c: GOLD },
              { time: '13:30', n: 'Wagner · Besichtigung', c: '#5cb87f' },
              { time: '16:00', n: 'Notar · A-2461', c: '#6e8aa3' },
            ].map((a) => (
              <div
                key={a.time}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: '#fff',
                  borderRadius: 10,
                  marginBottom: 6,
                  border: '1px solid #ece7dc',
                }}
              >
                <div style={{ width: 3, height: 28, background: a.c, borderRadius: 999 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#1a1715', fontWeight: 600 }}>{a.n}</div>
                  <div style={{ fontSize: 10, color: '#7a7068', marginTop: 2 }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            right: 12,
            height: 56,
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            borderRadius: 18,
            border: '1px solid #ece7dc',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          {['🏠', '👥', '🏛', '📅', '✦'].map((e, i) => (
            <div
              key={i}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                background: i === 0 ? 'rgba(201,162,95,0.18)' : 'transparent',
              }}
            >
              {e}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Security ──────────────────────────────────────────────────────────
function LandingSecurity() {
  const { t } = useTranslation();
  return (
    <section id="security" style={{ position: 'relative' }}>
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 py-16 sm:px-8 sm:py-20 md:!py-[120px] md:!px-12"
      >
      <ScrollReveal style={{ textAlign: 'center', marginBottom: 56 }}>
        <div
          style={{
            fontSize: 12,
            color: GOLD,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            fontWeight: 700,
          }}
        >
          {t('home.sec.eyebrow', 'Sicherheit & Compliance')}
        </div>
        <h2
          style={{
            margin: '12px 0 0',
            fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
            fontWeight: 300,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            fontSize: 'clamp(32px, 4.5vw, 56px)',
          }}
        >
          {t('home.sec.title_lead', 'Diskretion ist ')}
          <span style={{ fontStyle: 'italic', color: GOLD }}>
            {t('home.sec.title_accent', 'Pflicht.')}
          </span>
        </h2>
      </ScrollReveal>

      <AnimatedList
        className="grid grid-cols-2 lg:grid-cols-4"
        style={{ gap: 14 }}
        itemDelay={100}
      >
        {[
          { tt: 'DSGVO', d: t('home.sec.s1', '§ 5–9 GDPR · Datenresidenz Wien') },
          { tt: 'ISO 27001', d: t('home.sec.s2', 'Informations-Sicherheit zertifiziert') },
          { tt: 'BWG-konform', d: t('home.sec.s3', 'Bankwesen-Audit-Standards') },
          { tt: 'ImmoWAG', d: t('home.sec.s4', '§ 6 Audit-Trail vollständig') },
        ].map((c) => (
          <div
            key={c.tt}
            style={{
              padding: 28,
              background: SURFACE,
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 14,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 14px',
                borderRadius: 14,
                background: 'rgba(201,162,95,0.14)',
                border: '1px solid rgba(201,162,95,0.28)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: GOLD,
              }}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L4 5V11C4 16 7 20 12 22C17 20 20 16 20 11V5L12 2Z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <div
              style={{ fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif', fontSize: 22, letterSpacing: '-0.015em' }}
            >
              {c.tt}
            </div>
            <div style={{ fontSize: 12.5, color: TEXT_MUTED, marginTop: 6 }}>{c.d}</div>
          </div>
        ))}
      </AnimatedList>
      </div>
    </section>
  );
}

// ─── Workflow — vom Lead zum Notar ─────────────────────────────────────
function LandingWorkflow() {
  const { t } = useTranslation();
  const stages = [
    {
      n: '01',
      label: t('home.wf.s1_l', 'Lead'),
      title: t('home.wf.s1_t', 'Erstkontakt'),
      body: t(
        'home.wf.s1_d',
        'Zoki liest eingehende Anfragen, klassifiziert nach Bezirk und Profil und schlägt sofort den passenden Makler vor. Verteiler-Match in unter zwei Sekunden.',
      ),
    },
    {
      n: '02',
      label: t('home.wf.s2_l', 'Aufnahme'),
      title: t('home.wf.s2_t', 'Mandat unterschrieben'),
      body: t(
        'home.wf.s2_d',
        'Mobiler Foto-Upload vor Ort. Vermittlungsauftrag automatisch generiert mit WKO-Beilage, Klient unterschreibt per SMS-Magic-Link in unter sechs Minuten.',
      ),
    },
    {
      n: '03',
      label: t('home.wf.s3_l', 'Aktiv'),
      title: t('home.wf.s3_t', 'Vermarktung läuft'),
      body: t(
        'home.wf.s3_d',
        'Objekt sichtbar im 4M-Off-Market-Pool. Käufer-Matching gegen aktive Suchprofile — der Makler entscheidet, wann auf Willhaben oder ImmoScout publiziert wird. SMS-Vorlagen für Top-Treffer in einem Klick.',
      ),
    },
    {
      n: '04',
      label: t('home.wf.s4_l', 'Besichtigung'),
      title: t('home.wf.s4_t', 'Termine ohne Verwaltung'),
      body: t(
        'home.wf.s4_d',
        'Terminvorschläge per SMS, Erinnerungen 2 h vorher automatisch. Besichtigungsschein in 90 Sekunden generiert und elektronisch unterschrieben.',
      ),
    },
    {
      n: '05',
      label: t('home.wf.s5_l', 'Kaufanbot'),
      title: t('home.wf.s5_t', 'Beidseitig signiert'),
      body: t(
        'home.wf.s5_d',
        'Stepper-Anbot mit Provisions-Berechnung. Käufer und Verkäufer unterschreiben getrennt per Magic-Link. Status auf „akzeptiert" mit einem Klick.',
      ),
    },
    {
      n: '06',
      label: t('home.wf.s6_l', 'Notar'),
      title: t('home.wf.s6_t', 'Beurkundet & verbucht'),
      body: t(
        'home.wf.s6_d',
        'Notar-Termin mit allen Beteiligten in einem Klick. Provision automatisch verbucht, Klient-Akt geschlossen, Audit-Log abgelegt.',
      ),
    },
  ];

  return (
    <section id="workflow" style={{ position: 'relative' }}>
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 py-16 sm:px-8 sm:py-20 md:!py-[120px] md:!px-12"
      >
        <ScrollReveal style={{ marginBottom: 56 }}>
          <div
            style={{
              fontSize: 12,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              fontWeight: 700,
            }}
          >
            {t('home.wf.eyebrow', 'Der vollständige Verkaufszyklus')}
          </div>
          <h2
            style={{
              margin: '12px 0 0',
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontWeight: 300,
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
              maxWidth: 900,
              fontSize: 'clamp(32px, 4.6vw, 56px)',
            }}
          >
            {t('home.wf.title_lead', 'Vom Anruf bis zum Notartermin — ')}
            <span style={{ fontStyle: 'italic', color: GOLD }}>
              {t('home.wf.title_accent', 'ein')}
            </span>{' '}
            {t('home.wf.title_tail', 'System.')}
          </h2>
          <p
            style={{
              fontSize: 16.5,
              color: TEXT_DIM,
              marginTop: 20,
              lineHeight: 1.6,
              maxWidth: 720,
            }}
          >
            {t(
              'home.wf.body',
              'Sechs klar definierte Phasen. In jeder arbeitet die Plattform für Sie: Dokumente werden vorbereitet, Erinnerungen verschickt, Statuswechsel ausgelöst, Daten gesichert. Sie führen das Gespräch — wir kümmern uns um den Rest.',
            )}
          </p>
        </ScrollReveal>

        <AnimatedList
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          style={{ gap: 14 }}
          itemDelay={90}
        >
          {stages.map((s, i) => (
            <div
              key={s.n}
              style={{
                position: 'relative',
                padding: 24,
                background: SURFACE,
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 3,
                  height: '100%',
                  background: `linear-gradient(180deg, ${GOLD}, transparent)`,
                  opacity: 0.7,
                }}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <span
                  style={{
                    fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                    fontSize: 36,
                    color: GOLD,
                    fontStyle: 'italic',
                    fontWeight: 300,
                    lineHeight: 1,
                  }}
                >
                  {s.n}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: TEXT_MUTED,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    fontWeight: 700,
                  }}
                >
                  {s.label}
                </span>
              </div>
              <div
                style={{
                  fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                  fontSize: 22,
                  color: TEXT,
                  letterSpacing: '-0.015em',
                  marginBottom: 10,
                }}
              >
                {s.title}
              </div>
              <div style={{ fontSize: 13.5, color: TEXT_MUTED, lineHeight: 1.6 }}>
                {s.body}
              </div>
              {i < stages.length - 1 && (
                <div
                  aria-hidden
                  className="hidden lg:block"
                  style={{
                    position: 'absolute',
                    right: -1,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 14,
                    height: 1,
                    background: 'rgba(201,162,95,0.4)',
                  }}
                />
              )}
            </div>
          ))}
        </AnimatedList>
      </div>
    </section>
  );
}

// ─── Effizienz-Metriken ─────────────────────────────────────────────────
function LandingMetrics() {
  const { t } = useTranslation();
  const stats = [
    {
      value: '−4 h',
      unit: t('home.metric.unit_day', '/ Tag'),
      label: t('home.metric.s1_l', 'Weniger Verwaltung pro Makler'),
      body: t(
        'home.metric.s1_d',
        'Auto-PDFs, SMS-Erinnerungen und Zoki-Briefings ersetzen Word-Vorlagen, Excel-Listen und manuelle Kalender-Einträge.',
      ),
    },
    {
      value: '+38%',
      unit: '',
      label: t('home.metric.s2_l', 'Mehr abgeschlossene Termine / Monat'),
      body: t(
        'home.metric.s2_d',
        'Mobile-First-Workflow plus Property-Matching gibt jedem Makler täglich drei zusätzliche produktive Stunden im Außendienst.',
      ),
    },
    {
      value: '0',
      unit: '',
      label: t('home.metric.s3_l', 'Verlorene Klienten seit 2024'),
      body: t(
        'home.metric.s3_d',
        'Das Ownership-Lock-Modell mit Admin-Genehmigungs-Pfad verhindert, dass Klienten beim Makler-Wechsel zwischen die Stühle fallen.',
      ),
    },
    {
      value: '1,4 s',
      unit: '',
      label: t('home.metric.s4_l', 'Zoki-Antwortzeit auf Anfragen'),
      body: t(
        'home.metric.s4_d',
        'Wo steht Fall Müller? Wer sucht in 1190 ab 1,5 Mio.? Zoki recherchiert quer durch das System und antwortet auf österreichischem Deutsch.',
      ),
    },
  ];
  return (
    <section
      style={{
        position: 'relative',
        background:
          'linear-gradient(180deg, transparent 0%, rgba(201,162,95,0.04) 50%, transparent 100%)',
      }}
    >
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 py-16 sm:px-8 sm:py-20 md:!py-[120px] md:!px-12"
      >
        <ScrollReveal style={{ textAlign: 'center', marginBottom: 56 }}>
          <div
            style={{
              fontSize: 12,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              fontWeight: 700,
            }}
          >
            {t('home.metric.eyebrow', 'Was unsere Makler messen')}
          </div>
          <h2
            style={{
              margin: '12px 0 0',
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontWeight: 300,
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
              fontSize: 'clamp(32px, 4.6vw, 56px)',
            }}
          >
            {t('home.metric.title_lead', 'Mehr Termine, ')}
            <span style={{ fontStyle: 'italic', color: GOLD }}>
              {t('home.metric.title_accent', 'weniger')}
            </span>{' '}
            {t('home.metric.title_tail', 'Verwaltung.')}
          </h2>
          <p
            style={{
              fontSize: 16,
              color: TEXT_DIM,
              marginTop: 16,
              lineHeight: 1.55,
              maxWidth: 660,
              margin: '16px auto 0',
            }}
          >
            {t(
              'home.metric.body',
              'Konkrete Zahlen aus dem 4M-Team-Reporting Q1 2026. Gemessen über alle Makler, Bezirke und Geschäftsstellen.',
            )}
          </p>
        </ScrollReveal>

        <AnimatedList
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
          style={{ gap: 14 }}
          itemDelay={120}
        >
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                padding: 28,
                background: SURFACE,
                border: '1px solid rgba(201,162,95,0.18)',
                borderRadius: 14,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(201,162,95,0.18) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 4,
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                    fontSize: 'clamp(40px, 5.2vw, 60px)',
                    fontWeight: 300,
                    color: TEXT,
                    letterSpacing: '-0.04em',
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </span>
                {s.unit && (
                  <span
                    style={{
                      fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                      fontSize: 18,
                      color: GOLD,
                      fontStyle: 'italic',
                    }}
                  >
                    {s.unit}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: TEXT,
                  fontWeight: 600,
                  marginTop: 16,
                  position: 'relative',
                }}
              >
                {s.label}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: TEXT_MUTED,
                  lineHeight: 1.55,
                  marginTop: 8,
                  position: 'relative',
                }}
              >
                {s.body}
              </div>
            </div>
          ))}
        </AnimatedList>
      </div>
    </section>
  );
}

// ─── Vorher / Mit 4M ────────────────────────────────────────────────────
function LandingBeforeAfter() {
  const { t } = useTranslation();
  const rows = [
    {
      task: t('home.ba.r1_t', 'Aufnahme-Bewertung'),
      before: t(
        'home.ba.r1_b',
        '2–3 Tage Recherche, manuelle Vergleichsobjekt-Suche, Excel-Vorlagen.',
      ),
      after: t(
        'home.ba.r1_a',
        'Zoki liefert Marktwert plus Vergleichsobjekte in 30 Sekunden — direkt vor Ort am Tablet.',
      ),
      saved: t('home.ba.r1_s', '−98 % Zeit'),
    },
    {
      task: t('home.ba.r2_t', 'Vermittlungsauftrag erstellen'),
      before: t(
        'home.ba.r2_b',
        'Word-Vorlage, manuell ausfüllen, drucken, Klient zur Unterschrift treffen.',
      ),
      after: t(
        'home.ba.r2_a',
        'Auto-generiert mit WKO-Beilage, per E-Mail oder SMS verschickt, in unter 6 Minuten unterschrieben.',
      ),
      saved: t('home.ba.r2_s', '−93 % Zeit'),
    },
    {
      task: t('home.ba.r3_t', 'Käufer-Matching'),
      before: t(
        'home.ba.r3_b',
        'Excel-Liste durchgehen, anrufen, hoffen dass jemand passt.',
      ),
      after: t(
        'home.ba.r3_a',
        'Echtzeit-Match mit Begründung. SMS-Push an die Top-3-Treffer im Verteiler.',
      ),
      saved: t('home.ba.r3_s', 'Sofort statt Tage'),
    },
    {
      task: t('home.ba.r4_t', 'Termin-Erinnerungen'),
      before: t(
        'home.ba.r4_b',
        'Manuell SMS am Vortag schreiben, oft vergessen, No-Shows kosten Provision.',
      ),
      after: t(
        'home.ba.r4_a',
        'Twilio-SMS automatisch 2 h vor Termin. Bestätigung-Reply landet direkt im Akt.',
      ),
      saved: t('home.ba.r4_s', '−72 % No-Shows'),
    },
    {
      task: t('home.ba.r5_t', 'Kaufanbot bis Notar'),
      before: t(
        'home.ba.r5_b',
        'Anbot drucken, faxen, hin- und herschicken, Notar separat koordinieren.',
      ),
      after: t(
        'home.ba.r5_a',
        '5-Schritt-Stepper, beidseitige digitale Unterschrift, Notar-Termin per Knopfdruck angelegt.',
      ),
      saved: t('home.ba.r5_s', '−4 Tage Durchlaufzeit'),
    },
    {
      task: t('home.ba.r6_t', 'Provisionsabrechnung'),
      before: t(
        'home.ba.r6_b',
        'Nach Closing manuell aufteilen, Streit über Multi-Makler-Anteile, Excel-Listen.',
      ),
      after: t(
        'home.ba.r6_a',
        'Live-Provision pro Etappe automatisch berechnet, Multi-Makler-Anteile dokumentiert, jederzeit prüfbar.',
      ),
      saved: t('home.ba.r6_s', 'Null Streitfälle'),
    },
  ];

  return (
    <section style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div
        style={{ maxWidth: 1280, margin: '0 auto' }}
        className="px-6 py-16 sm:px-8 sm:py-20 md:!py-[120px] md:!px-12"
      >
        <ScrollReveal style={{ marginBottom: 48 }}>
          <div
            style={{
              fontSize: 12,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              fontWeight: 700,
            }}
          >
            {t('home.ba.eyebrow', 'Der Unterschied im Alltag')}
          </div>
          <h2
            style={{
              margin: '12px 0 0',
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontWeight: 300,
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
              maxWidth: 900,
              fontSize: 'clamp(32px, 4.6vw, 56px)',
            }}
          >
            {t('home.ba.title_lead', 'Was ')}
            <span style={{ fontStyle: 'italic', color: GOLD }}>
              {t('home.ba.title_accent', 'davor')}
            </span>{' '}
            {t('home.ba.title_tail', 'Stunden brauchte, dauert jetzt Minuten.')}
          </h2>
          <p
            style={{
              fontSize: 16.5,
              color: TEXT_DIM,
              marginTop: 20,
              lineHeight: 1.6,
              maxWidth: 720,
            }}
          >
            {t(
              'home.ba.body',
              'Sechs konkrete Aufgaben aus dem Maklerinnen-Alltag. Links: wie es vor unserer Plattform aussah. Rechts: wie es heute mit 4M Immo läuft. Die eingesparte Zeit fließt direkt in mehr Termine, mehr Abschlüsse, höhere Provision.',
            )}
          </p>
        </ScrollReveal>

        <AnimatedList style={{ display: 'flex', flexDirection: 'column', gap: 14 }} itemDelay={70}>
          {rows.map((r) => (
            <div
              key={r.task}
              style={{
                background: SURFACE,
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '14px 22px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
                    fontSize: 19,
                    color: TEXT,
                    letterSpacing: '-0.015em',
                    flex: 1,
                  }}
                >
                  {r.task}
                </div>
                <span
                  style={{
                    padding: '5px 12px',
                    background: 'rgba(92,184,127,0.16)',
                    color: '#5cb87f',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {r.saved}
                </span>
              </div>
              <div
                className="grid grid-cols-1 md:grid-cols-2"
                style={{ gap: 0 }}
              >
                <div
                  style={{
                    padding: '20px 22px',
                    borderRight: '1px solid rgba(255,255,255,0.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: '#c2452f',
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {t('home.ba.col_before', 'Vorher')}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: TEXT_MUTED,
                      lineHeight: 1.55,
                    }}
                  >
                    {r.before}
                  </div>
                </div>
                <div
                  style={{
                    padding: '20px 22px',
                    background: 'rgba(201,162,95,0.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: GOLD,
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {t('home.ba.col_after', 'Mit 4M')}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: TEXT,
                      lineHeight: 1.55,
                    }}
                  >
                    {r.after}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </AnimatedList>
      </div>
    </section>
  );
}

// ─── CTA ───────────────────────────────────────────────────────────────
function LandingCTA() {
  const { t } = useTranslation();
  return (
    <section
      id="cta"
      style={{
        padding: '80px 24px',
        background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
      className="md:!py-[100px] md:!px-12"
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 80% 50%, rgba(0,0,0,0.2) 0%, transparent 60%)',
        }}
      />
      <div
        className="grid grid-cols-1 lg:grid-cols-[2fr_1fr]"
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          gap: 40,
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <ScrollReveal>
          <h2
            style={{
              fontFamily: 'Fraunces, Lora, Noto Sans Arabic, Noto Sans Hebrew, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Noto Sans SC, Georgia, serif',
              fontWeight: 300,
              color: INK,
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
              margin: 0,
              fontSize: 'clamp(32px, 5vw, 60px)',
            }}
          >
            {t('home.cta.title_lead', 'Bereit, Ihre Immobilie')}
            <br />
            <span style={{ fontStyle: 'italic' }}>
              {t('home.cta.title_accent', 'diskret')}
            </span>{' '}
            {t('home.cta.title_tail', 'zu verkaufen?')}
          </h2>
          <div
            style={{
              fontSize: 17,
              color: '#3a2d10',
              marginTop: 22,
              lineHeight: 1.55,
              maxWidth: 600,
            }}
          >
            {t(
              'home.cta.body',
              'Persönliches Erstgespräch, vertraulich. Wir nennen Ihnen einen realistischen Preis, erklären unseren Off-Market-Prozess und zeigen Ihnen, wie unsere Plattform Ihre Daten schützt.',
            )}
          </div>
          {/* Five-point assurance list — aligned to the new CTA copy.
              Each pair is title + sub-line, rendered as a tight list with
              gold checkmark prefixes for warmth on the dark gold band. */}
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '28px 0 0',
              display: 'grid',
              gap: 14,
              maxWidth: 600,
            }}
          >
            {[1, 2, 3, 4, 5].map((n) => {
              const tt = t(`home.cta.p${n}_t`, '');
              const td = t(`home.cta.p${n}_d`, '');
              if (!tt) return null;
              return (
                <li key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span
                    aria-hidden
                    style={{
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: '#1a1715',
                      color: GOLD,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      marginTop: 2,
                    }}
                  >
                    ✓
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, color: INK, fontSize: 15 }}>{tt}</div>
                    {td && (
                      <div style={{ color: '#3a2d10', fontSize: 14, marginTop: 2, lineHeight: 1.45 }}>
                        {td}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <a
              href="mailto:office@4m-immo.at"
              style={{
                padding: '16px 24px',
                background: '#1a1715',
                color: TEXT,
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                textDecoration: 'none',
              }}
            >
              {t('home.cta.consult', 'Beratung anfragen')}
              <ArrowSvg />
            </a>
            <a
              href="tel:+4319050030"
              style={{
                padding: '16px 24px',
                background: 'transparent',
                color: INK,
                border: '1.5px solid #1a1715',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'center',
                textDecoration: 'none',
              }}
            >
              +43 1 905 00 30
            </a>
            <div style={{ fontSize: 12, color: '#3a2d10', marginTop: 6, textAlign: 'center' }}>
              {t('home.cta.address', 'Margaretenstraße 99 · 1050 Wien · Mo–Fr 09:00–18:00')}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────
function LandingFooter() {
  const { t } = useTranslation();
  return (
    <footer
      style={{
        padding: '60px 24px 40px',
        background: CANVAS,
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
      className="md:!px-12"
    >
      <div
        className="grid grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]"
        style={{ maxWidth: 1280, margin: '0 auto', gap: 32 }}
      >
        <div className="col-span-2 lg:col-span-1">
          <div style={{ marginBottom: 14 }}>
            <img
              src="/brand/logo-full.png"
              alt="4M Immobilien"
              style={{ height: 40, width: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </div>
          <div style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.6, maxWidth: 280 }}>
            {t(
              'home.footer.tagline',
              'Wiens diskreteste Immobilienagentur seit 2009. Spezialisiert auf Premium-Lagen, Off-Market-Geschäfte und institutionelle Verkäufer.',
            )}
          </div>
        </div>
        {[
          {
            h: t('home.footer.col1', 'Plattform'),
            items: [
              { label: t('home.footer.c1_1', 'Funktionen'), href: '#features' },
              // c1_2 was "Workflow → #workflow" until 2026-05-08; copy now reads
              // "Mobile Nutzung" so we anchor to the mobile/PWA section.
              { label: t('home.footer.c1_2', 'Mobile Nutzung'), href: '#mobile' },
              { label: t('home.footer.c1_3', 'Sicherheit'), href: '#security' },
              { label: t('home.footer.c1_4', 'Mitarbeiter-Login'), href: '/login' },
            ],
          },
          {
            h: t('home.footer.col2', 'Agentur'),
            items: [
              // External marketing site lives on 4m-immo.at; URLs verified by the
              // operator on 2026-05-08. Standorte points to the EDIreal listings
              // search portal instead of the marketing site.
              { label: t('home.footer.c2_1', 'Über uns'), href: 'https://4m-immo.at/immobilienmakler-in-wien-uber-uns/', external: true },
              { label: t('home.footer.c2_2', 'Team'), href: 'https://4m-immo.at/immobilienmakler-in-wien-uber-uns/#vorstand', external: true },
              { label: t('home.footer.c2_3', 'Standorte'), href: 'https://immobiliensuche.edireal.com/4m-immo.at/search', external: true },
              { label: t('home.footer.c2_4', 'Karriere'), href: 'https://4m-immo.at/karriere/', external: true },
            ],
          },
          {
            h: t('home.footer.col3', 'Service'),
            items: [
              { label: t('home.footer.c3_1', 'Verkaufen'), href: 'https://4m-immo.at/verkaufen', external: true },
              { label: t('home.footer.c3_2', 'Bewerten'), href: 'https://4m-immo.at/bewerten', external: true },
              { label: t('home.footer.c3_3', 'Beraten'), href: 'https://4m-immo.at/beratung', external: true },
              { label: t('home.footer.c3_4', 'Investment'), href: 'https://4m-immo.at/investment', external: true },
            ],
          },
          {
            h: t('home.footer.col4', 'Recht'),
            items: [
              { label: t('home.footer.c4_1', 'Impressum'), href: '/impressum' },
              { label: t('home.footer.c4_2', 'Datenschutz'), href: '/datenschutz' },
              { label: t('home.footer.c4_3', 'AGB'), href: 'https://4m-immo.at/agb', external: true },
              { label: t('home.footer.c4_4', 'DSGVO'), href: '/datenschutz' },
            ],
          },
        ].map((c) => (
          <div key={c.h}>
            <div
              style={{
                fontSize: 11,
                color: TEXT_FAINT,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              {c.h}
            </div>
            {c.items.map((i) => (
              <div key={i.label} style={{ fontSize: 13, marginBottom: 8 }}>
                <a
                  href={i.href}
                  {...((i as { external?: boolean }).external
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                  style={{ color: TEXT_DIM, textDecoration: 'none' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = TEXT)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_DIM)}
                >
                  {i.label}
                </a>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div
        className="flex flex-col md:flex-row md:justify-between"
        style={{
          maxWidth: 1280,
          margin: '40px auto 0',
          paddingTop: 24,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontSize: 12,
          color: TEXT_FAINT,
          gap: 8,
        }}
      >
        <span>
          © {new Date().getFullYear()} {COMPANY.legalName} · FB Nr. {COMPANY.legal.firmenbuchNr} ·
          UID {COMPANY.legal.uid}
        </span>
        <span>
          <a
            href="/impressum"
            style={{ color: TEXT_DIM, textDecoration: 'underline' }}
          >
            {t('home.footer.imprint', 'Impressum')}
          </a>{' '}
          ·{' '}
          <a
            href="/datenschutz"
            style={{ color: TEXT_DIM, textDecoration: 'underline' }}
          >
            {t('home.footer.privacy', 'Datenschutz')}
          </a>{' '}
          · {t('home.footer.crafted_lead', 'Crafted with')}{' '}
          <span style={{ color: GOLD }}>♥</span>{' '}
          {t('home.footer.crafted_in', 'in Belgrade, Serbia by')}{' '}
          <a
            href="https://remas.tech"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: GOLD, textDecoration: 'none', fontWeight: 500 }}
          >
            RemaS Technologies
          </a>
        </span>
      </div>
    </footer>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────
function ArrowSvg() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const navLink: CSSProperties = {
  color: TEXT_DIM,
  textDecoration: 'none',
  fontSize: 14,
  cursor: 'pointer',
};

const btnGold: CSSProperties = {
  padding: '9px 16px',
  background: GOLD,
  color: INK,
  border: 'none',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnOutline: CSSProperties = {
  padding: '9px 16px',
  background: 'transparent',
  color: TEXT,
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};
